import { UITable } from '../../components/table/ui-table';
import type {
    ComponentPlugin,
    LifeCycleHook,
} from '../../components/ui-element.type';
import { setAttributes, setInnerText } from '../../global/render-api';
import type { UITableJSONConfig } from './ui-table-json.type';

/**
 * @class
 * @memberof SharedComponents
 * @classdesc Class for ui-table json plugin.
 * @alias UITableJSON
 * @implements {ComponentPlugin}
 * @param {HTMLTableElement} target Target instance.
 */
export class UITableJSON implements ComponentPlugin {
    private readonly target: UITable;

    constructor(target: UITable) {
        this.target = target;
    }

    buildFromJson(config: UITableJSONConfig): void {
        const isConfigObject = typeof config === 'object';
        const isConfigArray = Array.isArray(config);
        if (!config || !isConfigObject || isConfigArray) {
            return;
        }

        if (config.caption) {
            this.target.table.appendChild(
                this.target.createElement({
                    tagName: 'caption',
                    children: [config.caption],
                })
            );
        }

        setAttributes(this.target.table, config.attributes ?? {});

        let row: HTMLTableRowElement;

        if (config.thead) {
            const thead: HTMLTableSectionElement =
                document.createElement('thead');
            this.target.table.appendChild(thead);
            for (const theadRow of config.thead) {
                row = thead.insertRow(-1);

                for (const cellConfig of theadRow) {
                    const th: HTMLTableCellElement =
                        document.createElement('th');
                    setInnerText(th, String(cellConfig.value));
                    setAttributes(th, cellConfig.attributes ?? {});
                    row.appendChild(th);
                }
            }
        }

        const bodyTags = ['tbody', 'tfoot'] as const;
        for (const section of bodyTags) {
            if (!config[section]) {
                continue;
            }

            const elem: HTMLTableSectionElement =
                document.createElement(section);
            this.target.table.appendChild(elem);

            for (const rowConfig of config[section]) {
                row = elem.insertRow(-1);
                for (const cellConfig of rowConfig) {
                    const cell: HTMLTableCellElement = row.insertCell(-1);
                    setInnerText(cell, String(cellConfig.value));
                    setAttributes(cell, cellConfig.attributes ?? {});
                }
            }
        }

        this.applyMethods('render');
        this.applyMethods('hydrate');
    }

    /**
     * Applies other plugins while json is ready.
     * @param {string} method - "render/hydrate"
     * @private
     */
    private applyMethods(method: LifeCycleHook): void {
        const plugins = this.target.getInstancePlugins();
        delete plugins.json;
        for (const plug in plugins) {
            /* istanbul ignore else */
            if (plugins.hasOwnProperty(plug)) {
                plugins[plug][method]?.();
            }
        }
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.target.table.classList.add('-json');
    }
}

UITable.registerPlugin('json', UITableJSON);
